/**
 * EquipmentService component handles the UI and logic for performing service operations on equipment entities.
 *
 * Features:
 * - Displays a modal form for entering service details such as Service Information, Last Service Date, Periodicity, Service Provider, Service Notes, and Service File Links.
 * - Manages state for all form fields and handles their changes.
 * - Calculates the next service date based on periodicity and last service date.
 * - Validates input fields, including URL format for file links.
 * - Handles change request logic when periodicity is changed.
 * - Submits the service data to the backend API.
 * - Provides user feedback via toast notifications.
 *
 * Props:
 * @param {Object} props - Component props.
 * @param {Object} props.isoData - Initial ISO data for the equipment.
 * @param {Object} props.entity - Equipment entity object.
 * @param {Object} props.ISOFields - Metadata fields for ISO service.
 * @param {boolean} props.show - Controls modal visibility.
 * @param {Function} props.closeModal - Function to close the modal, optionally with refresh.
 *
 * State:
 * - isoData: Current ISO data for the equipment.
 * - serviceData: Aggregated service data to be submitted.
 * - serviceInformation: Selected service information.
 * - periodicity: Selected periodicity (years).
 * - lastServiceDate: Date of last service.
 * - nextServiceDate: Calculated next service date.
 * - changeRequestFlag: Indicates if a change request is required.
 * - changeRequestFormCompleted: Indicates if the change request form is provided.
 * - serviceProvider: Selected service provider.
 * - serviceNotes: Selected service notes.
 * - serviceNoteComments: Comments for service notes.
 * - serviceFileLinks: Array of file links related to the service.
 *
 * Methods:
 * - handleServiceInformationChange: Handles changes to service information.
 * - handleLastServiceDateChange: Handles changes to last service date.
 * - updateNextServiceDate: Calculates and updates the next service date.
 * - handlePeriodicityChange: Handles changes to periodicity.
 * - handleServiceProviderChange: Handles changes to service provider.
 * - handleServiceNotesChange: Handles changes to service notes.
 * - handleServiceNoteComments: Handles changes to service note comments.
 * - handleServiceFileLinkTypeChange: Handles changes to file link type.
 * - handleServiceFileLinkChange: Handles changes to file link value.
 * - validateServiceFileLinkChange: Validates file link format.
 * - addFileLink: Adds a new file link entry.
 * - validateServiceData: Validates the entire form before submission.
 * - addEquipmentService: Submits the service data to the backend.
 * - close: Closes the modal.
 *
 * UI:
 * - Uses React Bootstrap Modal for the dialog.
 * - Uses MUI Autocomplete and TextField for input fields.
 * - Displays validation errors and disables submission if required fields are missing.
 * - Shows toast notifications for success and error messages.
 *
 * @returns {JSX.Element|null} Modal dialog for equipment service, or null if no ISO data.
 */
import { useState, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import { Api, Upload } from './interface';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import DatePicker from './datePicker';
import { addDateWithYears } from './date';
import { toast } from 'react-toastify';
import { ISO_SERVICE_INFORMATION } from './constants';
import Checkbox from '@mui/material/Checkbox';
import { Form } from 'react-bootstrap';
import { PlusCircle } from 'react-bootstrap-icons';
import EquipmentServiceFileLink from './equipmentServiceFileLink';
import { Button } from 'react-bootstrap';
import { isPDFFormat } from './validator';

export default function EquipmentService(props) {
  const [isoData, setIsoData] = useState(props.isoData || {});
  const [serviceData, setServiceData] = useState({});
  const [serviceInformation, setServiceInformation] = useState('');
  const [periodicity, setPeriodicity] = useState('');
  const [lastServiceDate, setLastServiceDate] = useState(null);
  const [nextServiceDate, setNextServiceDate] = useState(null);
  const [changeRequestFlag, setChangeRequestFlag] = useState(false);
  const [changeRequestFormCompleted, setChangeRequestFormCompleted] =
    useState(false);
  const [serviceProvider, setServiceProvider] = useState('');
  const [serviceNotes, setServiceNotes] = useState('');
  const [serviceNoteComments, setServiceNoteComments] = useState('');
  const [fileLinksValid, setFileLinksValid] = useState(true);
  const [serviceFileLinks, setServiceFileLinks] = useState([
    {
      id: 1,
      type: '',
      EntityMetaType_DBID: undefined,
      EntityMetaValue: undefined,
    },
  ]);

  useEffect(() => {
    if (props.isoData) {
      setIsoData(props.isoData);
      if (isoData.ServiceInformation) {
        setServiceInformation(isoData.ServiceInformation.EntityMetaValue);
      }
      if (isoData.Periodicity) {
        setPeriodicity(isoData.Periodicity.EntityMetaValue);
      }
    }
  }, [props.isoData, isoData]);

  const close = (refresh) => {
    props.closeModal(refresh);
  };

  /**
   * Handles changes to the service information field.
   *
   * Updates the local state for service information and service data.
   * If the new value matches the INDICATION constant, it resets the last and next service dates,
   * removes them from the service data object, and resets change request flags.
   *
   * @param {string|number|null} value - The new value for the service information field.
   */
  const handleServiceInformationChange = (value) => {
    setServiceInformation(value);
    let serviceDataUpdate = {
      ...serviceData,
      ServiceInformation: {
        EntityMetaType_DBID:
          props.ISOFields['Service Information'].EntityMetaType_DBID,
        EntityMetaValue: value ? value.toString() : '',
      },
    };
    setServiceData(serviceDataUpdate);

    if (value === ISO_SERVICE_INFORMATION.INDICATION) {
      setLastServiceDate(null);
      setNextServiceDate(null);
      let rest = { ...serviceDataUpdate };
      delete rest.LastServiceDate;
      delete rest.NextServiceDate;
      setServiceData(rest);
      setChangeRequestFlag(false);
      setChangeRequestFormCompleted(false);
    }
  };

  /**
   * Handles changes to the last service date input.
   *
   * - If the date is null, undefined, or an empty string, it resets both the last and next service dates,
   *   and removes these properties from the service data object.
   * - Otherwise, it sets the last service date and updates the next service date accordingly.
   *
   * @param {(string|Date|null|undefined)} date - The new last service date value. Can be a date string, Date object, null, or undefined.
   */
  const handleLastServiceDateChange = (date) => {
    if (date === null || date === undefined || date === '') {
      setLastServiceDate(null);
      setNextServiceDate(null);
      let serviceDataUpdate = { ...serviceData };
      delete serviceDataUpdate.LastServiceDate;
      delete serviceDataUpdate.NextServiceDate;
      setServiceData(serviceDataUpdate);
      return;
    }
    setLastServiceDate(date);
    updateNextServiceDate(date);
  };

  /**
   * Updates the next service date for an equipment entity based on the provided service date and periodicity.
   *
   * - Calculates the next service date by adding the periodicity (in years) to the given service date.
   * - Validates that the next service date is not in the past; if it is, shows a toast error and resets the date.
   * - Updates the service data state with new next and last service dates, and optionally updates periodicity.
   * - Sets a change request flag if the periodicity has changed.
   *
   * @param {Date|string} serviceDate - The date of the last service (can be a Date object or a date string).
   * @param {number|string} newPeriodicity - The new periodicity in years (can be a number or string).
   */
  const updateNextServiceDate = (serviceDate, newPeriodicity) => {
    let serviceDataUpdate = serviceData;
    const period = Number(newPeriodicity ?? periodicity);
    if (!isNaN(period) && period > 0 && serviceDate) {
      const nextDate = addDateWithYears(new Date(serviceDate), period);
      // Check if the next service date is in the past
      if (nextDate < new Date()) {
        let toastMsg = 'Next service day ';
        toastMsg += 'for ' + (props.entity?.EntityName || 'Equipment');
        toast.error(toastMsg + ' should be after current date');
        serviceDataUpdate.NextServiceDate = null;
        setNextServiceDate(null);
        setServiceData(serviceDataUpdate);
        return;
      }
      setNextServiceDate(nextDate);
      // update ISO data for the respective entity
      serviceDataUpdate = {
        ...serviceData,
        NextServiceDate: {
          EntityMetaType_DBID:
            props.ISOFields['Next Service Date'].EntityMetaType_DBID,
          EntityMetaValue: nextDate,
        },
        LastServiceDate: {
          EntityMetaType_DBID:
            props.ISOFields['Last Service Date'].EntityMetaType_DBID,
          EntityMetaValue: serviceDate,
        },
      };

      if (
        newPeriodicity !== isoData?.Periodicity?.EntityMetaValue &&
        newPeriodicity !== undefined
      ) {
        setChangeRequestFlag(true);
        serviceDataUpdate = {
          ...serviceDataUpdate,
          Periodicity: {
            EntityMetaType_DBID:
              props.ISOFields['Periodicity'].EntityMetaType_DBID,
            EntityMetaValue: newPeriodicity ? newPeriodicity.toString() : '',
          },
        };
      } else if (newPeriodicity === isoData?.Periodicity?.EntityMetaValue) {
        let rest = { ...serviceDataUpdate };
        delete rest.Periodicity;
        serviceDataUpdate = rest;
      }
    } else {
      setNextServiceDate(null);
    }
    setServiceData(serviceDataUpdate);
  };

  /**
   * Handles changes to the periodicity value for equipment service.
   *
   * This function updates the periodicity state and manages related flags and data
   * based on the new value and current service information. It performs the following:
   * - Sets the new periodicity value.
   * - If the service information is not an ISO indication:
   *   - If the new value is different from the existing periodicity and not null,
   *     it flags a change request and updates the next service date.
   *   - If the new value matches the existing periodicity, it resets change request flags
   *     and updates the next service date.
   *   - If the new value is null or undefined, it removes periodicity from service data,
   *     resets the next service date, and flags a change request.
   *
   * @param {any} value - The new periodicity value selected by the user.
   */
  const handlePeriodicityChange = (value) => {
    setPeriodicity(value);
    if (ISO_SERVICE_INFORMATION.INDICATION !== serviceInformation) {
      if (value !== isoData?.Periodicity?.EntityMetaValue && value !== null) {
        setChangeRequestFlag(true);
        updateNextServiceDate(lastServiceDate, value);
      } else if (value === isoData?.Periodicity?.EntityMetaValue) {
        setChangeRequestFlag(false);
        setChangeRequestFormCompleted(false);
        updateNextServiceDate(lastServiceDate, value);
      } else if (value === null || value === undefined) {
        let rest = { ...serviceData };
        delete rest.Periodicity;
        setNextServiceDate(null);
        setServiceData(rest);
        setChangeRequestFlag(true);
      }
    }
  };

  const handleServiceProviderChange = (value) => {
    setServiceProvider(value);
    let serviceDataUpdate = {
      ...serviceData,
      ServiceProvider: {
        EntityMetaType_DBID:
          props.ISOFields['Service Provider'].EntityMetaType_DBID,
        EntityMetaValue: value ? value.toString() : '',
      },
    };
    setServiceData(serviceDataUpdate);
  };

  /**
   * Handles changes to the service notes input field.
   *
   * Updates the local state for service notes and synchronizes the `serviceData` object accordingly.
   * - If the new value is `null` or `undefined`, clears the service note comments, removes
   *   `ServiceNotes` and `ServiceNoteComments` from `serviceData`, and updates the state.
   * - Otherwise, updates `serviceData` with the new `ServiceNotes` value, including its
   *   `EntityMetaType_DBID` and stringified value.
   *
   * @param {string|null|undefined} value - The new value for the service notes input.
   */
  const handleServiceNotesChange = (value) => {
    setServiceNotes(value);
    if (value === null || value === undefined) {
      handleServiceNoteComments('');
      let serviceDataUpdate = { ...serviceData };
      delete serviceDataUpdate.ServiceNotes;
      delete serviceDataUpdate.ServiceNoteComments;
      setServiceData(serviceDataUpdate);
    } else {
      let serviceDataUpdate = {
        ...serviceData,
        ServiceNotes: {
          EntityMetaType_DBID:
            props.ISOFields['Service Notes'].EntityMetaType_DBID,
          EntityMetaValue: value ? value.toString() : '',
        },
      };

      setServiceData(serviceDataUpdate);
    }
  };

  const handleServiceNoteComments = (value) => {
    setServiceNoteComments(value);
    let serviceDataUpdate = {
      ...serviceData,
      ServiceNoteComments: {
        EntityMetaType_DBID:
          props.ISOFields['Service Notes Comments'].EntityMetaType_DBID,
        EntityMetaValue: value ? value.toString() : '',
      },
    };
    setServiceData(serviceDataUpdate);
  };

  /**
   * Handles changes to the type of a service file link.
   *
   * - If `newType` is null or undefined, removes the file link from the list.
   * - Otherwise, updates the file link's type and its associated `EntityMetaType_DBID`.
   * - If all links are removed, resets the list to a default empty link object.
   * - Updates both the local state for service file links and the overall service data.
   *
   * @param {Object} fileLink - The file link object being updated.
   * @param {(string|null|undefined)} newType - The new type to assign to the file link, or null/undefined to remove it.
   *
   */
  const handleServiceFileLinkTypeChange = (fileLink, newType) => {
    let updatedLinks = serviceFileLinks;
    if (newType === null || newType === undefined) {
      updatedLinks = serviceFileLinks.filter((link) => link.id !== fileLink.id);
    } else {
      updatedLinks = serviceFileLinks.map((link) =>
        link.id === fileLink.id
          ? {
              ...link,
              type: newType,
              EntityMetaType_DBID: props.ISOFields[newType].EntityMetaType_DBID,
            }
          : link
      );
    }
    if (updatedLinks.length === 0) {
      updatedLinks = [
        {
          id: 1,
          type: '',
          EntityMetaType_DBID: undefined,
          EntityMetaValue: undefined,
        },
      ];
    }
    setServiceFileLinks(updatedLinks);

    let serviceDataUpdate = {
      ...serviceData,
      ServiceFileLinks: updatedLinks,
    };
    setServiceData(serviceDataUpdate);
  };

  const handleServiceFileLinkChange = (fileLink, newLink, file) => {
    let updatedLinks = serviceFileLinks.map((link) =>
      link.id === fileLink.id
        ? { ...link, EntityMetaValue: newLink, file: file }
        : link
    );
    setServiceFileLinks(updatedLinks);

    let serviceDataUpdate = {
      ...serviceData,
      ServiceFileLinks: updatedLinks,
    };
    setServiceData(serviceDataUpdate);
    setFileLinksValid(true);
  };

  const validateServiceFileLinkChange = (fileLink, newLink, file) => {
    if (!newLink || newLink.trim() === '' || !isPDFFormat(newLink?.trim())) {
      toast.error(`Invalid file for ${fileLink.type}`, {
        position: toast.POSITION.TOP_RIGHT,
      });
      setFileLinksValid(false);
      return;
    }
    handleServiceFileLinkChange(fileLink, newLink?.trim(), file);
  };

  /**
   * Adds a new file link object to the serviceFileLinks state.
   *
   * - Determines a unique `id` for the new link by finding the maximum existing id and incrementing it,
   *   or sets it to 1 if there are no existing links.
   * - Initializes the new link object with default empty values for `type`, `EntityMetaType_DBID`, and `EntityMetaValue`.
   * - Updates the state by appending the new link to the existing array of serviceFileLinks.
   */
  const addFileLink = () => {
    const newId =
      serviceFileLinks.length > 0
        ? Math.max(...serviceFileLinks.map((link) => link.id)) + 1
        : 1;

    const newLink = {
      id: newId,
      type: '',
      EntityMetaType_DBID: '',
      EntityMetaValue: '',
      file: null,
    };

    setServiceFileLinks([...serviceFileLinks, newLink]);
  };

  /**
   * Validates the equipment service data based on multiple business rules.
   *
   * The validation checks include:
   * - If ISO_SERVICE_INFORMATION.INDICATION is false, both lastServiceDate and nextServiceDate must be provided.
   * - serviceProvider must be defined and not an empty string.
   * - If serviceNotes are present, serviceNoteComments must also be provided.
   * - All entries in serviceFileLinks must have a valid EntityMetaValue if EntityMetaType_DBID is present.
   * - If changeRequestFlag is set, changeRequestFormCompleted must be true.
   * - If changeRequestFormCompleted is true, serviceFileLinks must contain a valid 'Change Request Form' link.
   *
   * @returns {boolean} Returns true if any validation rule fails, otherwise false.
   */
  const validateServiceData = () => {
    return (
      (!ISO_SERVICE_INFORMATION.INDICATION &&
        (!lastServiceDate || !nextServiceDate)) ||
      !serviceProvider ||
      serviceProvider === '' ||
      (serviceNotes && !serviceNoteComments) ||
      !fileLinksValid ||
      Object.values(serviceFileLinks).some(
        (field) =>
          field &&
          field.EntityMetaType_DBID &&
          (field.EntityMetaValue === undefined ||
            field.EntityMetaValue === null ||
            field.EntityMetaValue === '')
      ) ||
      (changeRequestFlag && !changeRequestFormCompleted) ||
      (changeRequestFormCompleted &&
        !serviceFileLinks.some(
          (link) =>
            link.type === 'Change Request Form' &&
            link.EntityMetaValue !== undefined &&
            link.EntityMetaValue !== null &&
            link.EntityMetaValue !== ''
        ))
    );
  };

  // Uploads files associated with service file links.
  // Returns updated links with uploaded file references.
  const uploadFiles = async () => {
    let updatedLinks = serviceFileLinks;
    let responseText = '';

    const promises = updatedLinks.map(async (link) => {
      if (link.file) {
        const response = await Upload(link.file);
        if (response?.error) {
          responseText = 'error';
        }
        if (response.status === 200) {
          responseText = response.data[0].blobName;
          console.log(response, responseText);
        }
        updatedLinks = updatedLinks.map((l) =>
          l.id === link.id ? { ...l, EntityMetaValue: responseText } : l
        );
      }
    });
    await Promise.all(promises);
    return updatedLinks;
  };

  /**
   * Validates the equipment service data before enabling the "Add Service" button.
   *
   * This function checks for the following validation rules:
   * - If service information is not INDICATION, both lastServiceDate and nextServiceDate must be present.
   * - Service provider must be selected and not empty.
   * - If service notes are provided, service note comments must also be present.
   * - All service file links with a defined EntityMetaType_DBID must have a non-empty EntityMetaValue.
   * - If a change request is flagged, the change request form must be completed.
   * - If the change request form is completed, there must be a valid 'Change Request Form' link.
   *
   * Returns true if any validation rule fails (disables submission), otherwise false.
   */
  const addEquipmentService = async () => {
    let validLinks = true;
    // Upload files and get updated links
    let uploadedLinks = await uploadFiles();
    // Validate uploaded links and update service file links state
    let updatedLinks = uploadedLinks.map((link) => {
      if (
        (link.EntityMetaValue && !isPDFFormat(link.EntityMetaValue)) ||
        link.EntityMetaValue === 'error'
      ) {
        toast.error("Error Uploading file for '" + link.type + "'");
        validLinks = false;
        return { ...link, EntityMetaValue: undefined };
      }
      return link;
    });
    setServiceFileLinks(updatedLinks);
    setServiceData({ ...serviceData, ServiceFileLinks: updatedLinks });
    if (!validLinks) {
      return;
    }
    const serviceDataJSON = [];
    // If service notes is "Change in Periodicity", ensure Periodicity is present
    if (
      serviceData.ServiceNotes &&
      serviceData.ServiceNotes.EntityMetaValue === 'Change in Periodicity' &&
      !serviceData.Periodicity
    ) {
      toast.error(
        'Periodicity needs to be updated when Service Notes is "Change in Periodicity"'
      );
      return;
    }
    Object.entries(serviceData).forEach(([key, value]) => {
      if (
        key !== 'ServiceFileLinks' &&
        value?.EntityMetaType_DBID !== undefined
      ) {
        serviceDataJSON.push({
          Entity_DBID: props.entity.Entity_DBID,
          EntityMetaType_DBID: value.EntityMetaType_DBID,
          EntityMetaValue: value.EntityMetaValue,
        });
      }
    });

    if (Array.isArray(serviceData.ServiceFileLinks)) {
      updatedLinks.forEach((fileLink) => {
        if (
          fileLink?.EntityMetaType_DBID !== undefined &&
          fileLink?.EntityMetaValue !== undefined &&
          fileLink?.EntityMetaValue !== ''
        ) {
          serviceDataJSON.push({
            Entity_DBID: props.entity.Entity_DBID,
            EntityMetaType_DBID: fileLink.EntityMetaType_DBID,
            EntityMetaValue: fileLink.EntityMetaValue,
          });
        }
      });
    }
    Api({
      sp: 'addEquipmentService',
      json: { serviceDataJSON: serviceDataJSON },
    }).then((response) => {
      if (!response || response?.error) {
        toast.error(
          'Error occurred while adding service for ' + props.entity.EntityName
        );
      } else {
        toast.success(
          'Service added successfully for ' + props.entity.EntityName
        );
        close(props.entity);
      }
    });
  };

  if (!isoData || Object.keys(isoData).length === 0) {
    return null;
  }

  return (
    <Modal
      show={props.show}
      onHide={() => close()}
      className="entity-modal"
      size="lg"
    >
      <Modal.Header closeButton="true">
        <Modal.Title>
          Perform Service for {props.entity?.EntityName || 'Equipment'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="mb-2">
          <h6>
            Service Information <span style={{ color: 'red' }}>*</span>
          </h6>

          <Autocomplete
            className="type-ahead"
            options={
              props.ISOFields?.['Service Information']?.EntityMetaOption || []
            }
            getOptionLabel={(option) => option?.toString() || ''}
            onChange={(_event, newValue) => {
              handleServiceInformationChange(newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Select Service Information"
              />
            )}
            value={serviceInformation || ''}
          />
        </div>
        <div className="mb-2">
          <h6>
            Last Service Date{' '}
            <span
              style={{ color: 'red' }}
              hidden={
                !serviceInformation ||
                serviceInformation === ISO_SERVICE_INFORMATION.INDICATION
              }
            >
              *
            </span>
          </h6>
          <DatePicker
            renderInput={(props) => <TextField {...props} />}
            label="Event Last Service Date"
            update={(val) => handleLastServiceDateChange(val)}
            value={lastServiceDate || null}
          />
        </div>
        <div className="mb-2">
          <h6>
            Periodicity (years){' '}
            <span
              style={{ color: 'red' }}
              hidden={
                !serviceInformation ||
                serviceInformation === ISO_SERVICE_INFORMATION.INDICATION
              }
            >
              *
            </span>
          </h6>

          <Autocomplete
            className="type-ahead"
            options={props.ISOFields['Periodicity']?.EntityMetaOption || []}
            getOptionLabel={(option) => option?.toString() || ''}
            onChange={(_event, newValue) => {
              handlePeriodicityChange(newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Select Periodicity (years)"
              />
            )}
            value={periodicity || ''}
          />
        </div>
        <div className="mb-2">
          {nextServiceDate ? (
            <div className="next-service-date">
              Next service date: {nextServiceDate?.toLocaleDateString()}
            </div>
          ) : null}
        </div>
        {changeRequestFlag ? (
          <div className="change-request-section">
            <h6>
              Have you completed a Change Request Form?
              <span style={{ color: 'red' }}>*</span>
            </h6>
            <Checkbox
              checked={changeRequestFormCompleted}
              onChange={(event) => {
                setChangeRequestFormCompleted(event.target.checked);
              }}
            />
          </div>
        ) : null}

        <div className="mb-2">
          <h6>
            Service Provider <span style={{ color: 'red' }}>*</span>
          </h6>

          <Autocomplete
            className="type-ahead"
            options={
              props.ISOFields?.['Service Provider']?.EntityMetaOption || []
            }
            getOptionLabel={(option) => option?.toString() || ''}
            onChange={(_event, newValue) => {
              handleServiceProviderChange(newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Select Service Provider"
              />
            )}
            value={serviceProvider || ''}
          />
        </div>

        <div className="mb-2">
          <h6>Service Notes</h6>
          <div className="change-request-section">
            <Autocomplete
              id="service-notes-autocomplete"
              className="w-20 type-ahead"
              options={
                props.ISOFields?.['Service Notes']?.EntityMetaOption || []
              }
              getOptionLabel={(option) => option?.toString() || ''}
              onChange={(_event, newValue) => {
                handleServiceNotesChange(newValue);
              }}
              /* Always keep the clear button visible */
              sx={{
                width: 300,
                "& button[title='Clear']": {
                  visibility: 'visible',
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Select Service Notes"
                />
              )}
              value={serviceNotes || ''}
            />
            <Form.Control
              id="service-note-comments-textarea"
              /* Highlight mandatory comments if service notes is selected */
              className={
                !serviceNotes || serviceNoteComments?.length > 2
                  ? 'w-75 overflow-hidden'
                  : 'w-75 overflow-hidden required-field-border'
              }
              as="textarea"
              rows="1"
              placeholder="Type..."
              // need keyup for tab/enter detection, blur for save, and change for value control on local element
              onChange={(e) => handleServiceNoteComments(e.target.value)}
              onBlur={(e) => handleServiceNoteComments(e.target.value)}
              value={serviceNoteComments || ''}
              // disable if no service notes selected
              disabled={!serviceNotes}
            />
          </div>
        </div>
        <div className="mb-2">
          <div className="change-request-section">
            <h6>
              Service File Links{' '}
              <span style={{ color: 'red' }} hidden={!changeRequestFlag}>
                *
              </span>
            </h6>
            <PlusCircle
              className="ms-auto pointer"
              color="blue"
              onClick={() => addFileLink()}
              size={18}
            />{' '}
          </div>
          {serviceFileLinks.map((fileLink, index) => (
            <div key={index} className="mb-2">
              <EquipmentServiceFileLink
                validateServiceFileLinkChange={(fileLink, newLink, file) =>
                  validateServiceFileLinkChange(fileLink, newLink, file)
                }
                handleServiceFileLinkTypeChange={(fileLink, newType) =>
                  handleServiceFileLinkTypeChange(fileLink, newType)
                }
                handleServiceFileLinkChange={(fileLink, newLink, file) =>
                  handleServiceFileLinkChange(fileLink, newLink, file)
                }
                fileLink={fileLink}
              />
            </div>
          ))}
        </div>
        <Modal.Footer>
          <div className="outer">
            <div className="confirm">
              <>
                <Button
                  variant="outline-primary"
                  onClick={() => addEquipmentService()}
                  disabled={validateServiceData()}
                >
                  Add Service
                </Button>
                <Button variant="outline-danger" onClick={() => close()}>
                  Cancel
                </Button>
              </>
            </div>
          </div>
        </Modal.Footer>
      </Modal.Body>
    </Modal>
  );
}
